import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useDataRefresh, DEFAULT_POLL_INTERVAL_MS } from '@/hooks/useDataRefresh';
import { Plus, Search, Images, Calendar, Edit, Image as ImageIcon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import type { Album, Gallery } from '@/types/legacy';
import { AlbumFormDialog } from '@/components/Albums/AlbumFormDialog';

export default function Albums() {
  const { userRole } = useAuth();
  const { toast } = useToast();
  const [albums, setAlbums] = useState<Album[]>([]);
  const [albumImages, setAlbumImages] = useState<Record<string, Gallery[]>>({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null);

  const fetchAlbums = async () => {
    try {
      const { data, error } = await api
        .from('albums')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAlbums(data || []);

      // Fetch images for each album
      if (data && data.length > 0) {
        const imagePromises = data.map(album =>
          api
            .from('gallery')
            .select('*')
            .eq('element_type', 'album')
            .eq('element_id', album.id)
            .order('created_at', { ascending: false })
        );

        const imageResults = await Promise.all(imagePromises);
        const imagesMap: Record<string, Gallery[]> = {};

        data.forEach((album, index) => {
          imagesMap[album.id] = imageResults[index].data || [];
        });

        setAlbumImages(imagesMap);
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los álbumes',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlbums();
  }, []);
  useDataRefresh(fetchAlbums, { pollIntervalMs: DEFAULT_POLL_INTERVAL_MS });

  const filteredAlbums = albums.filter(album =>
    album.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    album.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (album: Album) => {
    setSelectedAlbum(album);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedAlbum(null);
    setDialogOpen(true);
  };

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  const getTotalImages = () => {
    return Object.values(albumImages).reduce((sum, images) => sum + images.length, 0);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
              Álbumes de Fotos
            </h1>
            <p className="text-muted-foreground mt-2">
              Gestión de álbumes de imágenes organizados
            </p>
          </div>
          {canManage && (
            <Button onClick={handleNew} className="bg-gradient-magma hover:shadow-magma-hover">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Álbum
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Buscar álbumes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Álbumes</p>
                <p className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
                  {albums.length}
                </p>
              </div>
              <Images className="w-8 h-8 text-primary/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Activos</p>
                <p className="text-3xl font-bold text-success">
                  {albums.filter(a => a.is_active).length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Imágenes</p>
                <p className="text-3xl font-bold text-primary">
                  {getTotalImages()}
                </p>
              </div>
              <ImageIcon className="w-8 h-8 text-primary/50" />
            </div>
          </Card>
        </div>

        {/* Albums Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : filteredAlbums.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <Images className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              {searchTerm ? 'No se encontraron álbumes' : 'No hay álbumes creados'}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAlbums.map((album) => {
              const images = albumImages[album.id] || [];
              const previewImages = images.slice(0, 4);

              return (
                <Card key={album.id} className="glass-card overflow-hidden hover:shadow-magma transition-all">
                  {/* Album Preview Grid */}
                  <div className="grid grid-cols-2 gap-1 h-48 bg-muted/20">
                    {previewImages.length > 0 ? (
                      previewImages.map((image, index) => (
                        <div key={image.id} className="relative overflow-hidden">
                          <img
                            src={image.image_url}
                            alt={image.description || `Imagen ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))
                    ) : (
                      <div className="col-span-2 flex items-center justify-center">
                        <ImageIcon className="w-16 h-16 text-muted-foreground/30" />
                      </div>
                    )}
                  </div>

                  {/* Album Info */}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-lg font-semibold flex-1">{album.name}</h3>
                      {!album.is_active && (
                        <Badge variant="secondary">Inactivo</Badge>
                      )}
                    </div>

                    {album.description && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {album.description}
                      </p>
                    )}

                    <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
                      <div className="flex items-center gap-2">
                        <ImageIcon className="w-4 h-4" />
                        <span>{images.length} imágenes</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {formatDistanceToNow(new Date(album.created_at), {
                            addSuffix: true,
                            locale: es,
                          })}
                        </span>
                      </div>
                    </div>

                    {canManage && (
                      <div className="pt-3 border-t border-border/50 flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(album)}
                          className="flex-1"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Editar
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <AlbumFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        album={selectedAlbum}
        onSuccess={fetchAlbums}
      />
    </DashboardLayout>
  );
}
